import { OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType, TemplatePortal } from '@angular/cdk/portal';
import { ComponentRef, Injector } from '@angular/core';

import { Observable, of, Subject } from 'rxjs';

import { PhOverlayConfig } from './index';

export type PhOverlayCloseResult = void | string | boolean | Record<string, unknown> | object | null;

export class PhOverlayRef<Type> {
  private componentRef?: ComponentRef<Type>;
  private _afterClosed$: Subject<PhOverlayCloseResult> = new Subject<PhOverlayCloseResult>();
  private _injector?: Injector;
  private _reusable?: boolean;

  constructor(
    private ref: OverlayRef,
    private template: ComponentType<Type> | TemplatePortal<Type>
  ) {}

  init(injector: Injector, { reusable }: PhOverlayConfig): PhOverlayRef<Type> {
    this._injector = injector;
    this._reusable = reusable;

    return this;
  }

  updateData(data: { message: string }): void {
    const i = this.componentRef?.instance as { update: (data: { message: string }) => void };
    i?.update(data);
  }

  attach(data?: unknown): PhOverlayRef<Type> {
    if (this.ref.hasAttached()) {
      return this;
    }

    if (this.template instanceof TemplatePortal) {
      const templateRef = this.ref.attach<Type>(this.template);
      Object.assign(templateRef.context as object, data);
    } else {
      const portal = new ComponentPortal(this.template, null, this._injector);
      const componentRef = this.ref.attach<Type>(portal);
      Object.assign(componentRef.instance as object, data);
      this.componentRef = componentRef;
    }

    return this;
  }

  toggle(data?: unknown): PhOverlayRef<Type> {
    if (this.ref.hasAttached()) {
      this.close()
    } else {
      this.attach(data)
    }

    return this;
  }

  close(value: PhOverlayCloseResult = null): Observable<PhOverlayCloseResult> {
    if (this._reusable) {
      this.ref.detach();
    } else {
      this.ref.dispose();
    }

    this._afterClosed$.next(value);
    if (!this._reusable) {
      this._afterClosed$.complete();
    }

    return of(value);
  }

  afterClosed<T = void>(): Observable<T | PhOverlayCloseResult> {
    return this._afterClosed$.asObservable();
  }
}
